// StrategyDetailPage — AI Bot strategy detail
// Layout:
//   - Top nav: < Back | Strategy name | share
//   - Hero card with strategy emoji + tier + risk
//   - Big backtest chart (last 90 days)
//   - Stats: win rate, avg win, avg loss, profit factor, Sharpe
//   - Best pairs
//   - Compatible platforms
//   - "Use this strategy" CTA

import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BOT_STRATEGIES, PLATFORMS, TIERS, type TierKey, DEMO_CHALLENGE } from '../../data/platforms';
import { TierPill, PlatformLogo } from '../../components/Brand';
import { TfCard, TfSectionHeaderView, TfSparklineMarker, TfTimeRange, TfBadge } from '../../components/TfPrimitives';

const RANGE_OPTIONS = ['1M', '3M', '6M', '1Y', '2Y'] as const;

export function StrategyDetailPage() {
  const { key } = useParams();
  const [params] = useSearchParams();
  const initial = key || params.get('strategy') || 'breakout';
  const nav = useNavigate();
  const strategy = BOT_STRATEGIES.find(s => s.key === initial) || BOT_STRATEGIES[0];
  const [range, setRange] = useState<typeof RANGE_OPTIONS[number]>('3M');

  const userTier: TierKey = DEMO_CHALLENGE.tier;
  const tierOrder = { Beginner: 0, Mid: 1, Professional: 2 } as const;
  const locked = tierOrder[userTier] < tierOrder[strategy.tierRequired];

  // Backtest data
  const points = (() => {
    const n = 90;
    const out: number[] = [];
    let v = 10000;
    for (let i = 0; i < n; i++) {
      v += Math.sin(i * 0.2) * 200 + (Math.random() - 0.4) * 400;
      out.push(v);
    }
    return out;
  })();
  const finalPct = ((points[points.length - 1] - points[0]) / points[0]) * 100;

  // Demo stats
  const stats = {
    winRate: 0.66,
    avgWin: 142.30,
    avgLoss: -78.50,
    profitFactor: 1.84,
    sharpe: 1.92,
    maxDrawdown: -8.4,
    tradesPerWeek: 22,
  };

  return (
    <div className="min-h-screen bg-tf-bg pb-[calc(96px+env(safe-area-inset-bottom))]">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-tf-bg/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold text-tf-text-1">{strategy.name}</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" strokeLinecap="round" /></svg>
        </button>
      </div>

      {/* Hero */}
      <div className="mx-4 mt-4 overflow-hidden rounded-[20px] p-5 text-white" style={{ background: `linear-gradient(135deg, ${strategy.color} 0%, #1C1C1E 100%)` }}>
        <div className="flex items-center gap-3">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-[14px] text-[28px]"
            style={{ background: 'rgba(255,255,255,0.15)' }}
          >
            {strategy.icon}
          </div>
          <div>
            <h1 className="text-[22px] font-bold tracking-tight">{strategy.name}</h1>
            <div className="mt-0.5 flex items-center gap-2">
              <TfBadge variant={strategy.riskLevel === 'Low' ? 'success' : strategy.riskLevel === 'Medium' ? 'warning' : 'danger'}>
                {strategy.riskLevel} risk
              </TfBadge>
              {locked && <TfBadge variant="info">🔒 {strategy.tierRequired}</TfBadge>}
            </div>
          </div>
        </div>
        <p className="mt-3 text-[14px] text-white/90">{strategy.tagline}</p>
      </div>

      {/* Backtest chart */}
      <TfSectionHeaderView title="Backtest" viewAllLabel="Compare" onViewAll={() => alert('Compare backtests')} />
      <TfCard className="mx-4">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[11px] text-tf-text-3">90-day return</div>
            <div className={['text-[24px] font-bold tabular-nums', finalPct >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
              {finalPct >= 0 ? '+' : ''}{finalPct.toFixed(1)}%
            </div>
            <div className="text-[11px] text-tf-text-3">on $50K account</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] text-tf-text-3">Trades</div>
            <div className="text-[15px] font-semibold tabular-nums text-tf-text-1">287</div>
          </div>
        </div>
        <div className="-mx-1 mt-2">
          <TfSparklineMarker
            points={points}
            color={finalPct >= 0 ? '#34C759' : '#FF3B30'}
            height={120}
            markerIndex={Math.floor(points.length * 0.6)}
            markerLabel={`$${(points[Math.floor(points.length * 0.6)] / 1000).toFixed(1)}K`}
            markerPct={((points[Math.floor(points.length * 0.6)] - points[0]) / points[0]) * 100}
          />
        </div>
        <TfTimeRange value={range} onChange={setRange} options={[...RANGE_OPTIONS]} />
      </TfCard>

      {/* Description */}
      <div className="mx-4 mt-3">
        <p className="text-[14px] leading-6 text-tf-text-1">{strategy.description}</p>
      </div>

      {/* Stats grid */}
      <TfSectionHeaderView title="Performance" />
      <div className="mx-4 grid grid-cols-2 gap-2">
        <StatCard label="Win rate" value={(stats.winRate * 100).toFixed(1) + '%'} sub="Last 90 days" />
        <StatCard label="Avg win" value={'+$' + stats.avgWin.toFixed(2)} sub="Per trade" color="text-tf-profit" />
        <StatCard label="Avg loss" value={'$' + stats.avgLoss.toFixed(2)} sub="Per trade" color="text-tf-loss" />
        <StatCard label="Profit factor" value={stats.profitFactor.toFixed(2)} sub="Wins / losses" />
        <StatCard label="Sharpe" value={stats.sharpe.toFixed(2)} sub="Risk-adj return" />
        <StatCard label="Max drawdown" value={stats.maxDrawdown.toFixed(1) + '%'} sub="Worst peak-to-trough" color="text-tf-loss" />
      </div>

      {/* Best pairs */}
      <TfSectionHeaderView title="Best instruments" />
      <div className="mx-4 flex flex-wrap gap-2">
        {strategy.pairs.map((p) => (
          <span key={p} className="rounded-full bg-tf-text-1 px-3 py-1.5 text-[12px] font-bold text-white">
            {p}
          </span>
        ))}
      </div>

      {/* Compatible platforms */}
      <TfSectionHeaderView title="Works on platforms" viewAllLabel="View all" onViewAll={() => nav('/portal/platforms')} />
      <div className="mx-4 grid grid-cols-3 gap-2">
        {PLATFORMS.filter(p => p.botCompatibility.includes(strategy.key)).slice(0, 6).map((p) => (
          <button
            key={p.key}
            onClick={() => nav('/portal/platform/' + p.key)}
            className="flex flex-col items-center gap-1.5 rounded-[12px] border border-tf-border bg-tf-bg p-3 active:scale-[0.98]"
          >
            <PlatformLogo name={p.key} size={36} />
            <span className="text-[10px] font-semibold text-tf-text-1 truncate w-full text-center">{p.displayName}</span>
          </button>
        ))}
      </div>

      {/* CTA */}
      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-tf-border bg-tf-bg/95 backdrop-blur-xl" style={{ paddingBottom: 'calc(56px + env(safe-area-inset-bottom))' }}>
        <div className="mx-auto max-w-[640px] px-4 py-3 md:px-6">
          {locked ? (
            <button
              onClick={() => nav('/portal/tiers')}
              data-testid="strategy-upgrade"
              className="h-12 w-full rounded-full bg-tf-text-1 text-[16px] font-semibold text-white active:scale-[0.99]"
            >
              🔒 Upgrade to {strategy.tierRequired} to use
            </button>
          ) : (
            <button
              onClick={() => nav('/portal/bot?strategy=' + strategy.key)}
              data-testid="strategy-use"
              className="h-12 w-full rounded-full bg-tf-text-1 text-[16px] font-semibold text-white active:scale-[0.99]"
            >
              Use this strategy
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, color }: { label: string; value: string; sub: string; color?: string }) {
  return (
    <div className="rounded-[12px] border border-tf-border bg-tf-bg p-3">
      <div className="text-[11px] uppercase tracking-wide text-tf-text-3">{label}</div>
      <div className={['mt-1 text-[18px] font-bold tabular-nums', color || 'text-tf-text-1'].join(' ')}>{value}</div>
      <div className="text-[10px] text-tf-text-3">{sub}</div>
    </div>
  );
}
